/**
 * Cross-tab session synchronization over the `BroadcastChannel` API — no polling, no `localStorage`
 * hacks. A single named channel carries session lifecycle events between every open tab of the app
 * so that, e.g., logging out in one tab tears the session down in all the others immediately.
 *
 * Deliberately framework-agnostic and side-effect-free at import time beyond opening the channel:
 * `AuthProvider` (and, later, `ThemeModeProvider`) subscribe to react to peer-tab events, and post
 * their own. If `BroadcastChannel` is unavailable (older browsers, some embedded webviews) every
 * method degrades to a safe no-op — the app keeps working, only the cross-tab convenience is lost.
 *
 * Note: per the BroadcastChannel spec a channel never receives its own posted messages, so a tab
 * that posts `logout` will not re-handle it — only the *other* tabs do.
 */

export type SessionEventType = 'logout' | 'login' | 'profile-updated' | 'theme-changed';

export interface SessionEvent {
  type: SessionEventType;
  /** Optional event-specific data (e.g. the new theme preference for `theme-changed`). */
  payload?: unknown;
  /** Epoch ms the event was posted — handy for de-duping/ordering if ever needed. */
  at: number;
}

export type SessionEventHandler = (event: SessionEvent) => void;

const CHANNEL_NAME = 'willnq.session';

const isSupported = typeof BroadcastChannel !== 'undefined';

const channel: BroadcastChannel | null = isSupported ? new BroadcastChannel(CHANNEL_NAME) : null;
const handlers = new Set<SessionEventHandler>();

if (channel) {
  channel.onmessage = (event: MessageEvent) => {
    const data = event.data as SessionEvent | undefined;
    if (!data || typeof data.type !== 'string') return;
    for (const handler of handlers) handler(data);
  };
}

export const sessionChannel = {
  /** True when the browser supports cross-tab sync; false means every method is a no-op. */
  supported: isSupported,

  /** Broadcast a session event to every other open tab. No-op when unsupported. */
  post(type: SessionEventType, payload?: unknown): void {
    channel?.postMessage({ type, payload, at: Date.now() } satisfies SessionEvent);
  },

  /** Subscribe to events posted by other tabs. Returns an unsubscribe fn. No-op when unsupported. */
  subscribe(handler: SessionEventHandler): () => void {
    if (!channel) return () => undefined;
    handlers.add(handler);
    return () => handlers.delete(handler);
  },
};
